import { useCallback, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useUploadReelMutation } from 'api/reels'
import { addToast } from 'components/Toast/Toast'
import { useUserProfile } from 'store'

interface ShareReelProps {
  reel: any
  onSuccess?: () => void
}

export function useShareReel({
  reel,
  onSuccess
}: ShareReelProps) {
  const { t } = useTranslation()
  const userData = useUserProfile()!
  const navigate = useNavigate()

  const { mutate: uploadReel, isLoading } = useUploadReelMutation()

  // Synchronous guard against two clicks occurring before React rerenders.
  const inFlightRef = useRef(false)

  // Forces the button disabled immediately after the first click.
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleShareClick = useCallback(() => {
    if (userData.status === 'restricted') {
      addToast('error', t('postRestrictedAcc'))
      return
    }

    // Hard double-submit protection.
    if (inFlightRef.current || isSubmitting || isLoading) {
      return
    }

    // Lock immediately, before the mutation has time to update isLoading.
    inFlightRef.current = true
    setIsSubmitting(true)

    uploadReel(
      {
        ...reel,
        discovery: true
      },
      {
        onSuccess: () => {
          // IMPORTANT:
          // Do NOT unlock here.
          //
          // Once the reel has successfully been created, this button remains
          // disabled so the same payload cannot be submitted a second time.

          onSuccess?.()

          navigate(`/${userData.username}`, {
            state: { tabName: 'reels' }
          })
        },

        onError: () => {
          // The request genuinely failed, so allow the user to retry.
          inFlightRef.current = false
          setIsSubmitting(false)
        }
      }
    )
  }, [
    userData.status,
    userData.username,
    t,
    isSubmitting,
    isLoading,
    uploadReel,
    reel,
    onSuccess,
    navigate
  ])

  return {
    handleShareClick,
    isSubmitting: isSubmitting || isLoading
  }
}